import {
  AdsSpend,
  ClientsCharge,
  EmployeeCount,
  LeadsList,
  OnboardingGoal,
  WebsiteTraffic,
} from '@mm/state/model/CompanyModel';
import { assign } from '@xstate/immer';
import { send, StateNodeConfig } from 'xstate';
import {
  SignUpFlowEvents,
  SignUpFlowEventsTypes,
} from './signUpMachineEventTypes';
import {
  SignUpFlowContext,
  SignUpFlowStatesSchema,
  SIGN_UP_FLOW_STATES_NAME,
} from './signUpMachineTypes';

const isActiveStep = (context: SignUpFlowContext, step: number) => {
  return context.activeStep === step;
};

const isFBUser = (context: SignUpFlowContext) => {
  return context.isFBUser;
};

const isSQL = (context: SignUpFlowContext) => {
  const { company } = context;
  return company?.employeeCount !== EmployeeCount.optionOne
    ? !!(
        company?.goal === OnboardingGoal.outboundLeadGen ||
        company?.monthlyAdBudget === AdsSpend.optionThree ||
        (company?.websiteVisitors &&
          company?.websiteVisitors !== WebsiteTraffic.optionOne) ||
        company?.clientsCharge === ClientsCharge.optionThree ||
        company?.leadsList === LeadsList.optionThree
      )
    : false;
};

const isNewSignupFlow = (context: SignUpFlowContext) => {
  return context.isNewSignupFlow;
};

const setNextStep = send(SignUpFlowEventsTypes.SET_NEXT_STEP, { delay: 800 });

export const initState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.INIT],
  SignUpFlowEvents
> = {
  on: {
    '': [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.WELCOME],
        cond: cont => isActiveStep(cont, 0),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.JOB_ROLE],
        cond: cont => isActiveStep(cont, 1),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.INDUSTRY],
        cond: cont => isActiveStep(cont, 2),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.TEAM_SIZE],
        cond: cont => isActiveStep(cont, 3),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.GOAL],
        cond: cont => isActiveStep(cont, 4),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.MONTHLY_AD_BUDGET],
        cond: cont => isActiveStep(cont, 5),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
        cond: cont => isActiveStep(cont, 6),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.WEBSITE_TRAFFIC],
        cond: cont => isActiveStep(cont, 7),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.LEADS_LIST],
        cond: cont => isActiveStep(cont, 8),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
        cond: cont => isActiveStep(cont, 9),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CONNECT_CHANNELS],
        cond: cont => isActiveStep(cont, 10),
      },
    ],
  },
};

export const welcomeState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.WELCOME],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.START]: {
      target: [SIGN_UP_FLOW_STATES_NAME.JOB_ROLE],
      actions: assign((context, event) => {
        context.activeStep = 1;
        const { name, businessEmail, website, phoneNumber } = event.data;

        if (context.company) {
          const updatedFields = {
            name,
            businessEmail,
            website,
            phoneNumber,
          };
          context.company = {
            ...context.company,
            ...updatedFields,
          };
        }
      }),
    },
    [SignUpFlowEventsTypes.SET_COMPANY]: {
      actions: assign((context, event) => {
        context.company = event.data.company;
      }),
    },
  },
};

export const jobRoleState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.JOB_ROLE],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: {
      target: [SIGN_UP_FLOW_STATES_NAME.WELCOME],
      actions: assign(context => {
        context.activeStep = 0;
      }),
    },
    [SignUpFlowEventsTypes.SELECT_JOB_ROLE]: {
      actions: [
        assign((context, event) => {
          const { jobRole } = event.data;

          if (context.company) {
            context.company = {
              ...context.company,
              jobRole,
            };
          }
        }),
        setNextStep,
      ],
    },

    [SignUpFlowEventsTypes.SET_NEXT_STEP]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.INDUSTRY],
        cond: cont => !isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 2;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.GOAL],
        cond: cont => isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 4;
        }),
      },
    ],
  },
};

export const industryState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.INDUSTRY],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: {
      target: [SIGN_UP_FLOW_STATES_NAME.JOB_ROLE],
      actions: assign(context => {
        context.activeStep = 1;
      }),
    },
    [SignUpFlowEventsTypes.SELECT_INDUSTRY]: {
      actions: [
        assign((context, event) => {
          if (context.company) {
            context.company = {
              ...context.company,
              industry: event.data.industry,
            };
            context.stepOptions.isOtherIndustrySelected = false;
          }
        }),
        setNextStep,
      ],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.TEAM_SIZE],
        actions: assign(context => {
          context.activeStep = 3;
        }),
      },
    ],
    [SignUpFlowEventsTypes.SET_OTHER_INDUSTRY]: {
      actions: assign(context => {
        context.stepOptions.isOtherIndustrySelected = true;
        if (context.company) {
          context.company = {
            ...context.company,
            industry: '',
          };
        }
      }),
    },
  },
};

export const teamSizeState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.TEAM_SIZE],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: {
      target: [SIGN_UP_FLOW_STATES_NAME.INDUSTRY],
      actions: assign(context => {
        context.activeStep = 2;
      }),
    },
    [SignUpFlowEventsTypes.SELECT_TEAM_SIZE]: [
      {
        actions: [
          assign((context, event) => {
            if (context.company) {
              context.company = {
                ...context.company,
                employeeCount: event.data.employeeCount,
              };
            }
          }),
          setNextStep,
        ],
      },
    ],
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: {
      target: [SIGN_UP_FLOW_STATES_NAME.GOAL],
      actions: assign(context => {
        context.activeStep = 4;
      }),
    },
  },
};

export const goalState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.GOAL],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.TEAM_SIZE],
        cond: cont => !isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 3;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.JOB_ROLE],
        cond: cont => isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 1;
        }),
      },
    ],
    [SignUpFlowEventsTypes.SELECT_GOAL]: {
      actions: [
        assign((context, event) => {
          if (context.company) {
            context.company = {
              ...context.company,
              goal: event.data.goal,
            };
          }
        }),
        setNextStep,
      ],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
        cond: cont => isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 6;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.MONTHLY_AD_BUDGET],
        cond: cont => !isFBUser(cont) && !isSQL(cont),
        actions: assign(context => {
          context.activeStep = 5;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
        cond: cont => !isSQL(cont) && !isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 6;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
        cond: cont => isSQL(cont),
        actions: assign(context => {
          context.activeStep = 9;
        }),
      },
    ],
  },
};

export const monthlyAdBudgetState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.MONTHLY_AD_BUDGET],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.GOAL],
        actions: assign(context => {
          context.activeStep = 4;
        }),
      },
    ],
    [SignUpFlowEventsTypes.SELECT_MONTHLY_AD_BUDGET]: {
      cond: cont => !isFBUser(cont),
      actions: [
        assign((context, event) => {
          if (context.company) {
            context.company = {
              ...context.company,
              monthlyAdBudget: event.data.monthlyAdBudget,
            };
          }
        }),
        setNextStep,
      ],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
        cond: cont => !isSQL(cont),
        actions: assign(context => {
          context.activeStep = 6;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
        cond: cont => isSQL(cont),
        actions: assign(context => {
          context.activeStep = 9;
        }),
      },
    ],
  },
};

export const clientsChargeState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.GOAL],
        cond: cont => isFBUser(cont) || isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 4;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.MONTHLY_AD_BUDGET],
        cond: cont => !isFBUser(cont),
        actions: assign(context => {
          context.activeStep = 5;
        }),
      },
    ],
    [SignUpFlowEventsTypes.SELECT_CLIENTS_CHARGE]: {
      actions: [
        assign((context, event) => {
          const { clientsCharge } = event.data;

          if (context.company) {
            context.company = {
              ...context.company,
              clientsCharge,
            };
          }
        }),
        setNextStep,
      ],
    },

    [SignUpFlowEventsTypes.SET_NEXT_STEP]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.REDIRECT],
        cond: cont => isNewSignupFlow(cont),
        actions: assign(context => {
          context.activeStep = 0;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.WEBSITE_TRAFFIC],
        cond: cont => !isFBUser(cont) && !isSQL(cont),
        actions: assign(context => {
          context.activeStep = 7;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.LEADS_LIST],
        cond: cont => isFBUser(cont) && !isSQL(cont),
        actions: assign(context => {
          context.activeStep = 8;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
        cond: cont => isSQL(cont),
        actions: assign(context => {
          context.activeStep = 9;
        }),
      },
    ],
  },
};

export const websiteTrafficState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.WEBSITE_TRAFFIC],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
        actions: assign(context => {
          context.activeStep = 6;
        }),
      },
    ],
    [SignUpFlowEventsTypes.SELECT_MONTHLY_WEBSITE_TRAFFIC]: {
      actions: [
        assign((context, event) => {
          if (context.company) {
            context.company = {
              ...context.company,
              websiteVisitors: event.data.websiteVisitors,
            };
          }
        }),
        setNextStep,
      ],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.LEADS_LIST],
        cond: cont => !isSQL(cont),
        actions: assign(context => {
          context.activeStep = 8;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
        cond: cont => isSQL(cont),
        actions: assign(context => {
          context.activeStep = 9;
        }),
      },
    ],
  },
};

export const leadsListState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.LEADS_LIST],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: [
      {
        target: [SIGN_UP_FLOW_STATES_NAME.WEBSITE_TRAFFIC],
        cond: cont => !isFBUser(cont),
        actions: assign(context => {
          context.activeStep = 7;
        }),
      },
      {
        target: [SIGN_UP_FLOW_STATES_NAME.CLIENTS_CHARGE],
        cond: cont => isFBUser(cont),
        actions: assign(context => {
          context.activeStep = 6;
        }),
      },
    ],
    [SignUpFlowEventsTypes.SELECT_LEAD_LIST_SIZE]: {
      actions: [
        assign((context, event) => {
          if (context.company) {
            context.company = {
              ...context.company,
              leadsList: event.data.leadsList,
            };
          }
        }),
        setNextStep,
      ],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: {
      target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
      actions: assign(context => {
        context.activeStep = 9;
      }),
    },
  },
};

export const watchVideoOne: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.VIDEO],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: {
      target: [SIGN_UP_FLOW_STATES_NAME.LEADS_LIST],
      actions: assign(context => {
        context.activeStep = 8;
      }),
    },
    [SignUpFlowEventsTypes.WATCH_VIDEO]: {
      actions: [setNextStep],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: {
      target: [SIGN_UP_FLOW_STATES_NAME.CONNECT_CHANNELS],
      actions: assign(context => {
        context.activeStep = 10;
      }),
    },
  },
};

export const connectChannels: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.CONNECT_CHANNELS],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'updateCompany',
    src: 'updateCompanyService',
  },
  on: {
    [SignUpFlowEventsTypes.GO_BACK]: {
      target: [SIGN_UP_FLOW_STATES_NAME.VIDEO],
      actions: assign(context => {
        context.activeStep = 9;
      }),
    },
    [SignUpFlowEventsTypes.CONNECT_CHANNELS]: {
      actions: [setNextStep],
    },
    [SignUpFlowEventsTypes.SET_NEXT_STEP]: {
      target: [SIGN_UP_FLOW_STATES_NAME.REDIRECT],
      actions: assign(context => {
        context.activeStep = 0;
      }),
    },
  },
};

export const redirectState: StateNodeConfig<
  SignUpFlowContext,
  SignUpFlowStatesSchema['states'][SIGN_UP_FLOW_STATES_NAME.REDIRECT],
  SignUpFlowEvents
> = {
  invoke: {
    id: 'redirect',
    src: 'redirectService',
    onDone: {
      target: [SIGN_UP_FLOW_STATES_NAME.WELCOME],
      actions: assign(context => {
        context.company = undefined;
      }),
    },
  },
};
